import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';

import * as roles from 'core/model/roles';
import {
  Table, Title, Button, Tbody, Thead, Toolbox, Tr, Search, Pagination
} from 'table';
import { StaticRoutes } from '../../model/routes';
import { requestData, getData, getInfo } from 'core/ducks/list';
import { Loading } from 'core/components';
import { ErrorPage } from 'core/views/pages';
import { ExportToCSV } from 'core/model/lib';
import T from 'modules/i18n';

const Certificates = (props) => {
  const { profile, match, dispatch, history, i18n, list, pending } = props;
  const { messages = {} } = i18n || {};
  
  const initialState = {
    query: '',
    status: '',
    sort: 'created',
    sort_method: 'desc',
    httpStatus: 200,
    page: 1,
    refreshing: false
  };

  const [state, setState] = useState({
    ...initialState,
    status: (match.params && match.params.status) ? match.params.status : '',
    page: 1
  });

  const fields = useMemo(() => 
    profile.role === roles.AUTHORIZED
      ? ['round', 'primary_registry', /*'station_id',*/ 'serial', 'subject', 'external_url', 'created']
      : ['round', 'organization_name', /*'station_id',*/ 'organization_type', 'primary_registry', 'serial', 'subject', 'external_url', 'created'],
    [profile.role]
  );

  const layout = useMemo(() => {
    const baseLayout = {
      round: { sortable: true },
      organization_name: { sortable: true },
      organization_type: { type: 'translatable', sortable: true },
      primary_registry: { sortable: true },
      station_id: { sortable: true },
      serial: { sortable: true },
      subject: { sortable: true },
      external_url: { type: 'link', sortable: true },
      created: { type: 'date', sortable: true },
    };
    
    if (profile.role !== roles.AUTHORIZED) {
      baseLayout.reviewer = { sortable: true };
    }
    
    return baseLayout;
  }, [profile.role]);

  const certificateStatus = ['rejected', 'approved', 'completed', 'suspended'];

  const createUrl = useCallback((scope = '') => {
    const { query, sort, sort_method, page, status } = state;
    let url = `flows/certificate/page/${page}/sort/${sort}/sort_method/${sort_method}`;
    const queryParam = (query !== '') ? '/query/' + query : '';
    
    const filters = { status };
    let fq = [];
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        fq.push(`${key}:${value}`);
      }
    });
    
    if (fq.length > 0) {
      fq = `/fq/${fq.join(';')}`;
    } else {
      fq = '';
    }
    
    url += queryParam + fq;
    
    if (scope === 'csv') {
      url += '/limit/100000';
    }
    
    return url;
  }, [state]);

  const fetchData = useCallback(() => {
    const url = createUrl();
    dispatch(requestData('certificate', url))
      .catch(httpStatus => {
        setState(prev => ({ ...prev, httpStatus }));
      });
  }, [dispatch, createUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const { status: paramStatus } = match.params;
    if (paramStatus !== state.status || state.query !== state.query) {
      const newState = { page: 1 };
      
      if (paramStatus !== state.status) {
        newState.status = paramStatus;
      }
      
      setState(prev => ({ ...prev, ...newState }));
    }
  }, [match.params, state.query, state.status]);

  const handlePageChange = useCallback((page) => {
    setState(prev => ({ ...prev, page }));
  }, []);

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    
    setState(prev => ({ ...prev, [name]: value, page: 1 }));
    
    if (name === 'status') {
      let url = profile.role !== roles.AUTHORIZED 
        ? StaticRoutes.AdminCertificates 
        : StaticRoutes.Certificates;
        
      url += value ? `/status/${value}` : '';
      history.push(url);
    }
  }, [profile.role, history]);

  const handleToolboxReset = useCallback(() => {
    setState({ ...initialState, page: 1 });
    
    let url = profile.role !== roles.AUTHORIZED 
      ? StaticRoutes.AdminCertificates 
      : StaticRoutes.Certificates;
      
    history.push(url);
  }, [profile.role, history]);

  const handleCSVExport = useCallback(() => {
    const url = createUrl('csv');
    
    dispatch(requestData('csv', url)).then(() => {
      if (!props.list.csv || !props.list.csv.data || !props.list.csv.data.values) return;
      
      const data = Object.values(props.list.csv.data.values).map(row => 
        fields.map(col => 
          (layout[col] && layout[col].type === 'translatable' && messages[row[col]]) 
            ? messages[row[col]] 
            : row[col]
        )
      );
      
      const fieldLabels = fields.map(field => messages[field] || field);
      const csv = new ExportToCSV('certificates.csv', fieldLabels, data);
      csv.createLink();
    });
  }, [dispatch, createUrl, props.list.csv, fields, layout, messages]);

  const handleSortChange = useCallback((sort) => {
    setState(prev => {
      if (sort === prev.sort) {
        return { ...prev, sort_method: prev.sort_method === 'asc' ? 'desc' : 'asc' };
      } else {
        return { ...prev, sort };
      }
    });
  }, []);

  if (state.httpStatus !== 200) {
    return <ErrorPage status={state.httpStatus} />;
  }
  
  if (list.certificate.status !== 200 && list.certificate.status !== '') {
    return <ErrorPage status={list.certificate.status} />;
  }

  const data = props.data;
  const info = props.info || {};
  const currentPage = Number.isFinite(Number(info.page)) && Number(info.page) > 0
    ? Number(info.page)
    : 1;
  const totalPages = Number.isFinite(Number(info.total_pages)) && Number(info.total_pages) > 0
    ? Number(info.total_pages)
    : 1;

  return (
    <Row>
      <Col>
        <Table>
          <Title>
            <T>certificates</T>
            <Button type="toolbox" title="filters" className="float-right" />
          </Title>
          <Toolbox onReset={handleToolboxReset}>
            <Row>
              <Col xs="12" lg="12" className="form-group text-right">
                <Search 
                  placeholder={`${messages.search || 'search'}...`} 
                  onChange={handleFilterChange} 
                  name="query" 
                  value={state.query}
                />
                <Button 
                  type="csv" 
                  title={`${messages.export || 'export'} csv`} 
                  onClick={handleCSVExport}
                >
                  <T>export</T> csv
                </Button>
                <Button 
                  type="resetFilters" 
                  title={messages['reset filters'] || 'reset filters'}
                >
                  <T>reset</T>
                </Button>
              </Col>
            </Row>
          </Toolbox>
          <Thead>
            <Tr
              className="text-capitalize"
              data={fields}
              layout={layout}
              sortBy={state.sort}
              sortMethod={state.sort_method}
              onClick={handleSortChange}
            />
          </Thead>
          {
            pending ? <Loading /> :
              <Tbody refreshing={state.refreshing}>
                <Tr
                  data={data}
                  layout={layout}
                  order={fields}
                />
              </Tbody>
          }
          <Pagination
            className="mx-auto"
            page={currentPage}
            total={totalPages}
            onClick={(page) => {
              if (page !== currentPage)
                handlePageChange(page);
            }}
          />
        </Table>
      </Col>
    </Row>
  );
};

Certificates.propTypes = {
  withoutToolBox: PropTypes.bool
};

const mapStateToProps = (state) => ({
  profile: state.profile.user,
  list: state.list,
  pending: state.list.certificate.pending,
  data: getData(state, 'certificate'),
  info: getInfo(state, 'certificate'),
  viewData: state.update.response,
  http_status: state.update.status,
  i18n: state.i18n,
  user: state.profile.user
});

export default connect(mapStateToProps)(Certificates);